import { HighlightLegend } from '@/components/ECharts';
import { FullScreenProps, withFullScreen } from '@/components/FullScreen';
import { ToggleSelect } from '@/components/ToggleSelect';
import { WithLoader } from '@/components/WithLoader';
import { CompetitorsMediaSeasonalityGraph } from '@/dashboards/CompetitorsMediaInvestment/components/CompetitorsMediaSeasonalityGraph';
import { useCompetitorsMediaInvestmentContext } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { Typography } from '@analytical-alley/ui';
import EChartsReact from 'echarts-for-react';
import React, { useRef } from 'react';

type SeriesData = {
    name: string;
    color: string;
};

type ChartOptions = Array<SeriesData>;

export const CompetitorsMediaSeasonalitySectionBase = ({
    fullScreenButton,
}: FullScreenProps) => {
    const {
        dashboard: { competitorVariableName },
    } = useDashboardGlobalContext();
    const {
        selectedCategoryFilter,
        setSelectedCategory,
        selectedCompetitorFilter,
        setSelectedCompetitor,
        isLoading,
        isFetching,
        competitorsMediaInvestmentData: {
            totalInvestment,
            totalInvestmentByVariable,
        },
    } = useCompetitorsMediaInvestmentContext();

    const chartRef = useRef<EChartsReact>(null);
    const [selected, setSelected] = React.useState<string>('');
    const [seriesData, setSeriesData] = React.useState<SeriesData[]>([]);

    const onSelected = (value: string) => {
        setSelected((prev: string) => {
            if (prev || prev === value) {
                return '';
            }
            return value;
        });
    };

    const getChartOptions = (chartOptions: ChartOptions) => {
        setSeriesData(chartOptions);
    };

    const isEmptyResults =
        totalInvestment === 0 &&
        Object.keys(totalInvestmentByVariable).length === 0;

    if (isEmptyResults) {
        return (
            <div className="glass-tile p-6">
                <div className="flex justify-between mb-4">
                    <Typography variant="titleS">
                        Competitor’s Media Seasonality
                    </Typography>
                </div>
                <div className="relative w-full">
                    <Typography
                        className="font-mono text-center"
                        variant="bodyM"
                    >
                        Competitor’s investments are not available yet.
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="glass-tile p-6">
            <WithLoader isLoading={isLoading} isFetching={isFetching}>
                <div className="flex justify-between mb-4">
                    <Typography variant="titleS">
                        Competitor’s Media Seasonality
                    </Typography>
                    <div className="flex gap-3">
                        <div className="flex gap-3">
                            <ToggleSelect
                                items={selectedCategoryFilter.map((item) => ({
                                    label: item.name,
                                    value: item.name,
                                    checked: item.selected,
                                }))}
                                placement={'bottom-end'}
                                compact
                                label={'Media Category'}
                                showAllCheckbox={true}
                                onChange={(name, checked) => {
                                    setSelectedCategory(name, checked);
                                }}
                            />
                            <ToggleSelect
                                items={selectedCompetitorFilter
                                    ?.filter(
                                        (item) =>
                                            item.name !==
                                            competitorVariableName,
                                    )
                                    .map((item) => ({
                                        label: item.name,
                                        value: item.name,
                                        checked: item.selected,
                                    }))}
                                placement={'bottom-end'}
                                compact
                                label={'Competitors'}
                                showAllCheckbox={true}
                                onChange={(name, checked) => {
                                    setSelectedCompetitor(name, checked);
                                }}
                            />
                        </div>
                        {fullScreenButton}
                    </div>
                </div>
                <CompetitorsMediaSeasonalityGraph
                    chartRef={chartRef}
                    selected={selected}
                    onSelected={onSelected}
                    getChartOptions={getChartOptions}
                />
                <div className="relative mt-5 pl-14 pr-4 z-20">
                    <HighlightLegend
                        highlighted={selected}
                        legendItems={seriesData?.map((s) => ({
                            name: String(s.name),
                            highlighted: true,
                            color: s.color,
                        }))}
                        onClick={(name) => {
                            if (selected === name) {
                                setSelected('');
                                return;
                            }
                            setSelected(name);
                        }}
                    />
                </div>
            </WithLoader>
        </div>
    );
};

export const CompetitorsMediaSeasonalitySection = withFullScreen(
    'competitors-media-seasonality',
    CompetitorsMediaSeasonalitySectionBase,
);

CompetitorsMediaSeasonalitySection.displayName =
    'CompetitorsMediaSeasonalitySection';
